import React, { useState, useMemo } from 'react'
import {
  PaginationRes,
  GashLog,
  GDKError,
  PermissionType
} from '@golden/gdk-admin'
import Box from '@material-ui/core/Box'
import MuiButton from '@material-ui/core/Button'
import { useDebouncedCallback } from 'use-debounce/lib'
import { format } from 'date-fns'
import { searchToRequest } from './PlayerReportGashLogForm'
import CoreTable from '../../../default/present/CoreTable'
import LoadingAndErrorFrame from '../../../default/frames/LoadingAndErrorFrame'
import ScrollablePaper from '../../../default/present/ScrollablePaper'
import { useRequestFromSearch, useGetDataByPayload, usePaginationClickAndChangeUrl } from '../../../../utils/default/ComplexFlowHook'
import { usePageFlow } from '../../../../utils/default/PageFlowHook'
import useGDK from '../../../../providers/admin/gdk/useGDK'
import { createDefaultPaginationData, createTableData, formatMoney } from '../../../../utils/default/TableHelper'
import useT from '../../../../i18ns/admin/useT'
import { useCommonStyles } from '../../../../utils/admin/StyleHook'
import allRoute from '../../route/route'
import DateTime from '../../../default/present/DateTime'
import gashTransactionTypeName from '../../../../constants/admin/gashTransactionTypeName'
import PointsCell from '../../../default/present/PointsCell'
import useGlobalDialog from '../../../../providers/admin/dialog/useGlobalDialog'
import { createGlobalDialogConfig } from '../../../../utils/default/DialogHelper'
import { useChecker } from '../../../../utils/admin/AdminRouteHook'

interface RowType {
  id: number
  account: string
  action: string
  beforeBalance: string
  amount: React.ReactElement
  afterBalance: string
  transactionNumber: string
  createdAt: React.ReactElement
  operator: string
  memo: React.ReactElement
}
const Button = React.memo(MuiButton)
const PlayerReportGashLogTable: React.FC = () => {
  const classes = useCommonStyles()
  const { t } = useT()
  const gdk = useGDK()
  const globalDialog = useGlobalDialog()
  const [list, setList] = useState<PaginationRes<GashLog[]>>(createDefaultPaginationData([]))

  const pageFlow = usePageFlow()
  const request = useRequestFromSearch({ searchToRequest })
  useGetDataByPayload({
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    payload: request!,
    gdkFunc: (payload) => gdk.history.getGashLog({ ...payload, perPage: 25 }),
    gdkFuncDependencies: [gdk],
    onBeforeFetch: pageFlow.setLoadingStart,
    onSuccess: (res: PaginationRes<GashLog[]>) => {
      setList(res)
      pageFlow.setContentShow()
    },
    onError: pageFlow.setGDKError,
    canLoadData: request !== undefined
  })
  const tableClasses = useMemo(() => ({
    head: classes.pinkTableHead,
    row: classes.tableRow,
    cellHead: classes.tableCellHead
  }), [classes])
  const rows: RowType[] = useMemo(() => {
    return list.data.map((item, index) => {
      return {
        id: index,
        account: item.account,
        action: t(gashTransactionTypeName[item.action]),
        beforeBalance: formatMoney(item.before_balance),
        amount: (<PointsCell points={item.amount} showSign />),
        afterBalance: formatMoney(item.after_balance),
        transactionNumber: item.transaction_number,
        createdAt: (<DateTime time={item.created_at} />),
        operator: item.operator,
        memo: (<span style={{ whiteSpace: 'pre-line' }}>{item.memo}</span>)
      } as RowType
    })
  }, [list.data, t, classes])
  const data = useMemo(() => {
    return createTableData<RowType>(
      {
        id: {
          label: '',
          value: 'id'
        },
        account: {
          label: t('common.playerAccount'),
          value: 'account',
          align: 'center'
        },
        action: {
          label: t('common.transactionType'),
          value: 'action',
          align: 'center'
        },
        beforeBalance: {
          label: t('common.beforeBalance'),
          value: 'beforeBalance',
          align: 'center'
        },
        amount: {
          label: t('common.deltaBalance'),
          value: 'amount',
          align: 'center'
        },
        afterBalance: {
          label: t('common.afterBalance'),
          value: 'afterBalance',
          align: 'center'
        },
        transactionNumber: {
          label: t('common.serialNumber'),
          value: 'transactionNumber',
          align: 'center'
        },
        createdAt: {
          label: t('common.updateAt'),
          value: 'createdAt',
          align: 'center',
          width: 120
        },
        operator: {
          label: t('common.updateBy'),
          value: 'operator',
          align: 'center'
        },
        memo: {
          label: t('common.memo'),
          value: 'memo',
          align: 'center',
          width: 150
        }
      },
      [
        'account',
        'transactionNumber',
        'action',
        'beforeBalance',
        'amount',
        'afterBalance',
        'createdAt',
        'operator',
        'memo'
      ],
      rows,
      'id'
    )
  }, [rows, t])
  const handlePagination = usePaginationClickAndChangeUrl({
    request,
    encodePath: allRoute.playerReportGashLog.encodePath
  })

  const [handleDebouncedExport] = useDebouncedCallback(() => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    gdk.history.exportGashLog(request!)
      .subscribe({
        next: (blob) => {
          const url = URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.download = `${t('page.gashLog')} ${format(new Date(), 'yyyy-MM-dd HH_mm_ss')}.csv`
          link.click()
          link.remove()
          URL.revokeObjectURL(url)
        },
        error: (err: GDKError) => {
          globalDialog.setConfig(createGlobalDialogConfig({
            variant: 'error',
            message: err.message,
            showCancel: false
          }))
          globalDialog.setOpen({ id: 'exportGashLogFailed', value: true, isOK: false })
        }
      })
  }, 200)

  const exportable = useChecker([PermissionType.PLAYER_REPORT_GASH_TRANSACTION_RECORD_EXPORT])

  if (request === undefined) return null
  return (
    <>
      { exportable && data.rows.length && (
        <Box paddingX={5} paddingTop={3} display='flex' justifyContent='flex-end'>
          <Button
            onClick={handleDebouncedExport}
            className={classes.purpleGradualButton}
          >
            {t('common.export')}
          </Button>
        </Box>
      )}
      <Box marginTop={3}>
        <ScrollablePaper marginX={5}>
          <Box padding={4}>
            <LoadingAndErrorFrame {...pageFlow.status}>
              <CoreTable
                classes={tableClasses}
                data={data}
                total={list.total}
                showPagination
                page={request.page}
                perPage={list.per_page}
                onChangePage={handlePagination}
              />
            </LoadingAndErrorFrame>
          </Box>
        </ScrollablePaper>
      </Box>
    </>
  )
}

export default React.memo(PlayerReportGashLogTable)
